import { useParams, useNavigate, Link } from 'react-router-dom';
import { useStockItem } from '@saiens/shared/hooks';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { ArrowLeft, Pencil } from 'lucide-react';

const STATUS_LABELS: Record<string, string> = {
  in_stock: '在庫',
  reserved: '已預定',
  scrapped: '已報廢',
};

const STATUS_COLORS: Record<string, string> = {
  in_stock: 'bg-green-500 text-white',
  reserved: 'bg-orange-400 text-white',
  scrapped: 'bg-red-500 text-white',
};

const BRAND_COLORS: Record<string, string> = {
  QJ: 'bg-pink-500 text-white',
  MQ: 'bg-blue-500 text-white',
  EG: 'bg-green-500 text-white',
  ET: 'bg-purple-500 text-white',
};

const THICKNESS_LABELS: Record<string, string> = {
  '10': '10mm', '12': '12mm', '15': '15mm',
  '17': '17mm', '20': '20mm', '30': '30mm',
};

const WAREHOUSE_LABELS: Record<string, string> = {
  yuli_longtang: '玉禮龍潭加工廠',
  other: '其他',
};

const SCRAP_LABELS: Record<string, string> = {
  bent: '明顯彎曲變形',
  broken: '表面破損/缺角/破孔',
  yellowed: '板材變黃/光化',
  other: '其他',
};

export function StockDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const numericId = id ? parseInt(id, 10) : undefined;
  const { data: item, isLoading, error } = useStockItem(numericId);

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner />
      </div>
    );
  }

  if (error || !item) {
    return (
      <div className="px-4 py-6">
        <button onClick={() => navigate(-1)} className="mb-4 flex items-center gap-1 text-sm text-gray-500">
          <ArrowLeft className="h-4 w-4" /> 返回
        </button>
        <div className="rounded-lg bg-white py-12 text-center text-red-500 shadow-sm">載入失敗</div>
      </div>
    );
  }

  return (
    <div className="pb-8 px-4 py-6">
      {/* Header */}
      <div className="mb-4 flex items-start justify-between">
        <div>
          <button onClick={() => navigate(-1)} className="mb-2 flex items-center gap-1 text-sm text-gray-500">
            <ArrowLeft className="h-4 w-4" /> 返回
          </button>
          <h1 className="text-lg font-bold text-gray-900">Saiens 餘料庫存</h1>
        </div>
        <Link
          to={`/stock/${item.id}/edit`}
          className="flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          <Pencil className="h-4 w-4" /> 編輯
        </Link>
      </div>

      {/* === 餘料資訊 === */}
      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
        {/* Section header */}
        <div className="bg-gray-700 px-4 py-2 text-center text-sm font-semibold text-white">
          餘料資訊
        </div>

        {/* Two-column table layout */}
        <div className="divide-y divide-gray-200">
          {/* Row 1: 編號 | 目前位置 */}
          <div className="grid grid-cols-2 divide-x divide-gray-200">
            <TableCell label="編號(識別條碼)" value={item.code} required />
            <TableCell label="目前位置" value={item.current_location} required />
          </div>

          {/* Row 2: 廠牌 | 備註 */}
          <div className="grid grid-cols-2 divide-x divide-gray-200">
            <TableCell label="廠牌" required>
              {item.brand_code && (
                <span className={`inline-block rounded-full px-3 py-0.5 text-xs font-bold ${BRAND_COLORS[item.brand_code] || 'bg-gray-500 text-white'}`}>
                  {item.brand_code}
                </span>
              )}
            </TableCell>
            <TableCell label="備註" value={item.notes} />
          </div>

          {/* Row 3: 型號 | 狀態 */}
          <div className="grid grid-cols-2 divide-x divide-gray-200">
            <TableCell label="型號" value={item.model_number} required />
            <TableCell label="狀態" required>
              {item.status && (
                <span className={`inline-block rounded-full px-3 py-0.5 text-xs font-bold ${STATUS_COLORS[item.status] || 'bg-gray-400 text-white'}`}>
                  {STATUS_LABELS[item.status] || item.status}
                </span>
              )}
            </TableCell>
          </div>

          {/* Row 4: 山恩案件編號 | 是否需報廢 */}
          <div className="grid grid-cols-2 divide-x divide-gray-200">
            <TableCell label="山恩案件編號" value={item.case_number} />
            <TableCell label="是否需報廢？" value={item.needs_scrap ? '是' : '否'} />
          </div>

          {/* Row 5: 長邊尺寸 | 短邊尺寸 */}
          <div className="grid grid-cols-2 divide-x divide-gray-200">
            <TableCell label="長邊尺寸(mm)" value={item.long_side ? `${item.long_side}mm` : ''} required />
            <TableCell label="短邊尺寸(mm)" value={item.short_side ? `${item.short_side}mm` : ''} required />
          </div>

          {/* Row 6: 厚度 | 不規則與否 */}
          <div className="grid grid-cols-2 divide-x divide-gray-200">
            <TableCell label="厚度" value={item.thickness_mm ? THICKNESS_LABELS[item.thickness_mm] || `${item.thickness_mm}mm` : ''} required />
            <TableCell label="不規則與否" value={item.is_irregular ? '是' : '否'} required />
          </div>

          {/* Row 7: 照片 */}
          {item.has_photo && (
            <div className="grid grid-cols-2 divide-x divide-gray-200">
              <div className="p-3">
                <p className="mb-1 text-xs text-gray-500">條碼</p>
                <div className="flex items-center justify-center rounded bg-gray-50 p-2">
                  <span className="font-mono text-sm font-bold tracking-widest">{item.code}</span>
                </div>
              </div>
              <div className="p-3">
                <p className="mb-1 text-xs text-gray-500">照片</p>
                <img
                  src={`/api/saiens/stock/items/${item.id}/photo`}
                  alt="餘料照片"
                  className="max-h-40 rounded object-contain"
                />
              </div>
            </div>
          )}

          {/* Row 8: 是否有污染或缺陷 */}
          <div className="grid grid-cols-2 divide-x divide-gray-200">
            <TableCell label="是否有污染或缺陷？" value={item.has_defect ? '是' : '否'} required />
            <div />
          </div>

          {/* Row 9: 報廢原因（if needs_scrap） */}
          {item.needs_scrap && item.scrap_reason && (
            <div className="grid grid-cols-2 divide-x divide-gray-200">
              <TableCell label="報廢原因" value={SCRAP_LABELS[item.scrap_reason] || item.scrap_reason} />
              {item.scrap_reason === 'other' && item.scrap_reason_note ? (
                <TableCell label="報廢原因說明" value={item.scrap_reason_note} />
              ) : (
                <div />
              )}
            </div>
          )}
        </div>
      </div>

      {/* === 預定資訊（if reserved） === */}
      {item.status === 'reserved' && (item.reserved_by || item.reserved_case) && (
        <div className="mt-4 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
          <div className="bg-orange-500 px-4 py-2 text-center text-sm font-semibold text-white">
            預定資訊
          </div>
          <div className="divide-y divide-gray-200">
            <div className="grid grid-cols-2 divide-x divide-gray-200">
              <TableCell label="預定者" value={item.reserved_by} />
              <TableCell label="預定案名" value={item.reserved_case} />
            </div>
          </div>
        </div>
      )}

      {/* === 底部資訊 === */}
      <div className="mt-4 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
        <div className="divide-y divide-gray-200">
          <div className="grid grid-cols-2 divide-x divide-gray-200">
            <TableCell label="倉庫位置" value={item.warehouse_location ? WAREHOUSE_LABELS[item.warehouse_location] || item.warehouse_location : ''} />
            <TableCell label="建立日期" value={item.write_date ? new Date(item.write_date).toLocaleString('zh-TW') : ''} />
          </div>
        </div>
      </div>
    </div>
  );
}

function TableCell({
  label,
  value,
  required,
  children,
}: {
  label: string;
  value?: string | number;
  required?: boolean;
  children?: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-0.5 p-3">
      <span className="text-xs text-gray-500">
        {required && <span className="text-red-500">*</span>}
        {label}
      </span>
      {children || (
        <span className="text-sm font-medium text-gray-900">
          {value || '—'}
        </span>
      )}
    </div>
  );
}
